// src/components/TrainerDashboard/PMCPaneAmCharts.jsx
import React, { useEffect, useMemo, useState, useLayoutEffect, useRef } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { useTranslation } from '../../i18n';

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5exporting from "@amcharts/amcharts5/plugins/exporting";
import API_BASE_URL from '../../config/api';

const API = `${API_BASE_URL.replace(/\/+$/, '')}/api`;

const IA_PROVIDER = (typeof window !== 'undefined' && localStorage.getItem('iaProvider')) || 'groq';
const IA_QUERY = `?provider=${encodeURIComponent(IA_PROVIDER)}`;
const FRONT_MASTER_TIMEOUT_MS = Number(
  (typeof window !== 'undefined' && localStorage.getItem('iaTimeoutMs')) || 120000
);

// Devuelve claves de traducción en lugar de strings directos
const analyzeTSB = (todayTsb) => {
  if (todayTsb <= -20) return { levelKey: 'pmcPane.tsb.highRiskLevel', msgKey: 'pmcPane.tsb.highRiskMsg' };
  if (todayTsb <= -10) return { levelKey: 'pmcPane.tsb.tiredLevel',    msgKey: 'pmcPane.tsb.tiredMsg' };
  if (todayTsb <= +10) return { levelKey: 'pmcPane.tsb.optimalLevel',  msgKey: 'pmcPane.tsb.optimalMsg' };
  return               { levelKey: 'pmcPane.tsb.freshLevel',           msgKey: 'pmcPane.tsb.freshMsg' };
};

export default function PMCPaneAmCharts({ athleteId }) {
  const { t } = useTranslation();

  // ====== Estado base ======
  const [data, setData]       = useState([]);
  const [loading, setLoading] = useState(false);

  const [daysRange, setDaysRange] = useState(90);
  const startISO = useMemo(() => dayjs().subtract(daysRange, 'day').startOf('day').toISOString(), [daysRange]);
  const endISO   = useMemo(() => dayjs().endOf('day').toISOString(), []);
  const today    = dayjs().format('YYYY-MM-DD');

  const [whatIf, setWhatIf] = useState({
    enabled: true, futureDays: 7, totalTss: 600,
    pattern: 'polarized', atlConst: 7, ctlConst: 42,
  });

  const initialH = Math.max(520, ((typeof window !== 'undefined' ? window.innerHeight : 740) - 260));
  const [chartHeight, setChartHeight] = useState(initialH);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setChartHeight(Math.max(480, window.innerHeight - 260));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ====== KPIs ======
  const todayRow = useMemo(() => {
    const row = [...data].reverse().find(d => dayjs(d.date).format('YYYY-MM-DD') === today);
    return row || data[data.length - 1];
  }, [data, today]);

  const todayTsb = todayRow?.tsb ?? 0;
  const hint     = analyzeTSB(todayTsb);

  const latest       = data?.[data.length - 1];
  const sevenDaysAgo = dayjs().subtract(7, 'day').startOf('day').valueOf();
  const closest7d    = [...data].reverse().find(d => d.date <= dayjs().endOf('day').valueOf() && d.date >= sevenDaysAgo);
  const ctlToday     = latest?.ctl ?? null;
  const ctl7d        = closest7d?.ctl ?? null;
  const rampCTL      = (ctlToday != null && ctl7d != null) ? (ctlToday - ctl7d) : null;

  const rampFlag =
    rampCTL == null ? null :
    rampCTL > 8    ? { levelKey: 'pmcPane.ramp.highLevel',     msgKey: 'pmcPane.ramp.highMsg' } :
    rampCTL > 5    ? { levelKey: 'pmcPane.ramp.moderateLevel', msgKey: 'pmcPane.ramp.moderateMsg' } :
    rampCTL > 0    ? { levelKey: 'pmcPane.ramp.softLevel',     msgKey: 'pmcPane.ramp.softMsg' } :
                     { levelKey: 'pmcPane.ramp.declineLevel',  msgKey: 'pmcPane.ramp.declineMsg' };

  // ====== Fetch ======
  useEffect(() => {
    let alive = true;
    const fetchPMC = async () => {
      if (!athleteId) return;
      setLoading(true);
      try {
        try { await axios.post(`${API}/performance/recalculate/${athleteId}`, { start: startISO, end: endISO }); }
        catch (_) { /* no-op */ }
        const resp = await axios.get(`${API}/performance/pmc/${athleteId}`, { params: { start: startISO, end: endISO } });
        const rows = (resp.data || []).map(r => ({
          date: new Date(r.date).getTime(),
          ctl: Number(r.ctl), atl: Number(r.atl), tsb: Number(r.tsb),
        }));
        if (alive) setData(rows);
      } catch (e) {
        console.error('❌ PMC fetch error:', e);
        if (alive) setData([]);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchPMC();
    return () => { alive = false; };
  }, [athleteId, startISO, endISO]);

  // ====== amCharts ======
  const chartDivRef      = useRef(null);
  const axesRef          = useRef({ xAxis: null, yLeft: null, yRight: null, chart: null, root: null });
  const seriesRef        = useRef({});
  const markerRangesRef  = useRef([]);
  const weekendRangesRef = useRef([]);
  const forecastRangeRef = useRef(null);

  useLayoutEffect(() => {
    if (!chartDivRef.current) return;

    const root = am5.Root.new(chartDivRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    root._logo?.dispose?.();

    const chart = root.container.children.push(am5xy.XYChart.new(root, {
      panX: true, panY: false, wheelX: "panX", wheelY: "zoomX",
      layout: root.verticalLayout,
      paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0,
    }));
    chart.setAll({ paddingLeft: 0, paddingRight: 0, paddingTop: 0, paddingBottom: 0 });
    chart.chartContainer.setAll({ paddingLeft: 0, paddingRight: 0 });

    const xAxis = chart.xAxes.push(am5xy.DateAxis.new(root, {
      baseInterval: { timeUnit: "day", count: 1 },
      renderer: am5xy.AxisRendererX.new(root, { minGridDistance: 40 }),
      extraMin: 0, extraMax: 0,
    }));

    const yLeft = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      renderer: am5xy.AxisRendererY.new(root, {}),
      min: 0, strictMinMax: false
    }));

    const yRightRenderer = am5xy.AxisRendererY.new(root, { opposite: true, inside: true, paddingLeft: 0, paddingRight: 0 });
    const yRight = chart.yAxes.push(am5xy.ValueAxis.new(root, {
      renderer: yRightRenderer, min: -50, max: 50, strictMinMax: true
    }));
    yRightRenderer.labels.template.setAll({ maxWidth: 40, oversizedBehavior: "truncate" });

    const r0 = yRight.createAxisRange(yRight.makeDataItem({ value: 0 }));
    r0.get("grid").setAll({ strokeOpacity: 0.6, strokeDasharray: [3, 3] });

    const makeTSBBand = (from, to, color, opacity = 0.06, label) => {
      const item  = yRight.makeDataItem({ value: from, endValue: to });
      const range = yRight.createAxisRange(item);
      range.get("axisFill").setAll({ fill: am5.color(color), fillOpacity: opacity });
      range.get("grid").setAll({ strokeOpacity: 0 });
      if (label) range.get("label").setAll({ text: label, dy: -2, opacity: 0.75, fontSize: 10, fill: am5.color(color) });
    };
    makeTSBBand(-1000, -20, "#ef4444", 0.08, "TSB ≤ -20 (riesgo)");
    makeTSBBand(-20,  -10,  "#f59e0b", 0.07, "-20 … -10");
    makeTSBBand(-10,   10,  "#22c55e", 0.06, "-10 … +10");
    makeTSBBand( 10, 1000,  "#3b82f6", 0.05, "≥ +10");

    const makeTSBGuide = (value, label, color) => {
      const di  = yRight.makeDataItem({ value });
      const rr  = yRight.createAxisRange(di);
      rr.get("grid").setAll({ stroke: am5.color(color), strokeDasharray: [4, 4], strokeOpacity: 0.95 });
      rr.get("label").setAll({
        text: label, dx: -6, opacity: 0.95, fontSize: 10, fill: am5.color(color),
        background: am5.RoundedRectangle.new(root, { fill: am5.color(color), fillOpacity: 0.05 })
      });
      return rr;
    };
    makeTSBGuide(-10, "TSB -10", "#f59e0b");
    makeTSBGuide(+10, "TSB +10", "#22c55e");

    chart.set("cursor", am5xy.XYCursor.new(root, { xAxis }));
    chart.set("scrollbarX", am5.Scrollbar.new(root, { orientation: "horizontal" }));

    const createSeries = (name, field, color, yAxis, dashed = false, fillUnder = false) => {
      const s = chart.series.push(am5xy.LineSeries.new(root, {
        name, xAxis, yAxis, valueXField: "date", valueYField: field,
        tooltip: am5.Tooltip.new(root, { labelText: `${name}: {valueY.formatNumber('#.0')}` })
      }));
      s.strokes.template.setAll({ strokeWidth: 2.5, stroke: am5.color(color), opacity: 1 });
      if (dashed) s.strokes.template.setAll({ strokeDasharray: [7, 5] });
      if (fillUnder) s.fills.template.setAll({ visible: true, fillOpacity: 0.15, fill: am5.color(color) });
      return s;
    };

    const CTL_COLOR = "#2563eb";
    const ATL_COLOR = "#d97706";
    const TSB_COLOR = "#7c3aed";

    const sATL     = createSeries(t('pmcPane.seriesFatigue'),  "atl", ATL_COLOR, yLeft, false, true);
    const sCTL     = createSeries(t('pmcPane.seriesFitness'),  "ctl", CTL_COLOR, yLeft, false, false);
    const sTSB     = createSeries(t('pmcPane.seriesForm'),     "tsb", TSB_COLOR, yRight, false, false);
    const sCTLProj = createSeries(t('pmcPane.seriesCtlProj'),  "ctl", CTL_COLOR, yLeft, true);
    const sATLProj = createSeries(t('pmcPane.seriesAtlProj'),  "atl", ATL_COLOR, yLeft, true);
    const sTSBProj = createSeries(t('pmcPane.seriesTsbProj'),  "tsb", TSB_COLOR, yRight, true);

    seriesRef.current = { atl: sATL, ctl: sCTL, tsb: sTSB, ctlProj: sCTLProj, atlProj: sATLProj, tsbProj: sTSBProj };
    axesRef.current   = { xAxis, yLeft, yRight, chart, root };

    const legend = chart.children.push(am5.Legend.new(root, { useDefaultMarker: true }));
    legend.data.setAll([sATL, sCTL, sTSB, sCTLProj, sATLProj, sTSBProj]);

    am5exporting.Exporting.new(root, {
      menu: am5exporting.ExportingMenu.new(root, {}),
      filePrefix: "banister-ctl-atl-tsb"
    });

    chart.appear(200, 50);
    return () => { root.dispose(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ===== what-if =====
  function buildDistribution(total, days, pattern) {
    if (days <= 0 || total <= 0) return Array.from({ length: Math.max(0, days) }, () => 0);
    if (pattern === 'even' || days !== 7) {
      const each = total / days;
      return Array.from({ length: days }, () => each);
    }
    const weights = [0.40, 0.25, 0.00, 0.20, 0.15, 0.00, 0.00];
    const sum = weights.reduce((a, b) => a + b, 0);
    return weights.map(w => (w / sum) * total);
  }

  function projectPMC(lastPoint, arrTss, atlConst, ctlConst) {
    if (!lastPoint) return [];
    let { date, atl, ctl } = lastPoint;
    const out = [];
    for (let i = 0; i < arrTss.length; i++) {
      const tss = Number(arrTss[i] || 0);
      date = dayjs(date).add(1, 'day').valueOf();
      atl  = atl + (tss - atl) / atlConst;
      ctl  = ctl + (tss - ctl) / ctlConst;
      out.push({ date, atl, ctl, tsb: ctl - atl });
    }
    return out;
  }

  const clearRanges = (arrRef) => {
    if (!arrRef?.current) return;
    arrRef.current.forEach(r => { try { r.dispose(); } catch(e) {} });
    arrRef.current = [];
  };

  const [projData, setProjData] = useState([]);

  const applyWeekendBands = () => {
    const { xAxis } = axesRef.current || {};
    if (!xAxis || !data?.length) return;
    clearRanges(weekendRangesRef);
    const start = dayjs(data[0].date).startOf('day');
    const endCandidate = (projData?.length ? projData[projData.length - 1].date : data[data.length - 1].date);
    const end = dayjs(endCandidate).endOf('day');
    let d = start.clone();
    while (d.isBefore(end)) {
      if (d.day() === 6) {
        const from = d.valueOf();
        const to   = d.add(2, 'day').valueOf();
        const di   = xAxis.makeDataItem({ value: from, endValue: to });
        const range = xAxis.createAxisRange(di);
        range.get("axisFill").setAll({ fill: am5.color("#f59e0b"), fillOpacity: 0.06 });
        range.get("grid").setAll({ strokeOpacity: 0 });
        weekendRangesRef.current.push(range);
        d = dayjs(to);
        continue;
      }
      d = d.add(1, 'day');
    }
  };

  const shadeForecast = () => {
    const { xAxis } = axesRef.current || {};
    if (!xAxis || !data?.length) return;
    if (forecastRangeRef.current) { try { forecastRangeRef.current.dispose(); } catch(e) {} forecastRangeRef.current = null; }
    if (!projData?.length) return;
    const from = data[data.length - 1].date;
    const to   = projData[projData.length - 1].date;
    const di   = xAxis.makeDataItem({ value: from, endValue: to });
    const range = xAxis.createAxisRange(di);
    range.get("axisFill").setAll({ fill: am5.color("#60a5fa"), fillOpacity: 0.08 });
    range.get("grid").setAll({ strokeOpacity: 0 });
    range.get("label").setAll({ text: t('pmcPane.seriesProjection'), dy: -12, fontSize: 10, opacity: 0.8 });
    forecastRangeRef.current = range;
  };

  useEffect(() => {
    const { atl, ctl, tsb, ctlProj, atlProj, tsbProj } = seriesRef.current || {};
    if (!atl || !ctl || !tsb) return;

    atl.data.setAll(data); ctl.data.setAll(data); tsb.data.setAll(data);

    if (axesRef.current?.yLeft && data.length) {
      const maxLeft  = Math.max(...data.map(d => Math.max(d.atl, d.ctl)));
      const paddedMax = Math.ceil((maxLeft * 1.15) / 10) * 10;
      axesRef.current.yLeft.setAll({ max: paddedMax, strictMinMax: false });
    }

    if (axesRef.current?.yRight && data.length) {
      const tsbVals = data.map(d => d.tsb);
      let minTSB = Math.min(...tsbVals, -40);
      let maxTSB = Math.max(...tsbVals,  40);
      if (projData?.length) {
        const pVals = projData.map(d => d.tsb);
        minTSB = Math.min(minTSB, ...pVals);
        maxTSB = Math.max(maxTSB, ...pVals);
      }
      const absMax = Math.max(Math.abs(minTSB), Math.abs(maxTSB), 30);
      const bound  = Math.ceil(absMax / 10) * 10;
      axesRef.current.yRight.setAll({ min: -bound, max: bound, strictMinMax: true });
    }

    if (whatIf.enabled && data.length) {
      const last = data[data.length - 1];
      const dist = buildDistribution(whatIf.totalTss, whatIf.futureDays, whatIf.pattern);
      const sim  = projectPMC({ ...last }, dist, whatIf.atlConst, whatIf.ctlConst);
      setProjData(sim);
      if (ctlProj && atlProj && tsbProj) {
        ctlProj.data.setAll(sim); atlProj.data.setAll(sim); tsbProj.data.setAll(sim);
      }
    } else {
      setProjData([]);
      if (seriesRef.current.ctlProj) {
        seriesRef.current.ctlProj.data.setAll([]);
        seriesRef.current.atlProj.data.setAll([]);
        seriesRef.current.tsbProj.data.setAll([]);
      }
    }

    applyWeekendBands();
    shadeForecast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, whatIf]);

  const tsbProjectedEnd = projData.length ? projData[projData.length - 1].tsb : null;

  // ====== IA: Diagnóstico ======
  const [diag, setDiag]               = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError]     = useState('');
  const [showMarkers, setShowMarkers] = useState(true);
  const trainerId = useMemo(() => localStorage.getItem('userId') || null, []);

  const clearMarkers = () => {
    if (!markerRangesRef.current) return;
    markerRangesRef.current.forEach(r => { try { r.dispose(); } catch(e) {} });
    markerRangesRef.current = [];
  };

  const severityColor = (sev) =>
    sev === 'risk'  ? '#ef4444' :
    sev === 'warn'  ? '#f59e0b' :
    sev === 'fresh' ? '#10b981' : '#94a3b8';

  const applyMarkers = (markers = []) => {
    const { xAxis, root } = axesRef.current || {};
    if (!xAxis || !root) return;
    clearMarkers();
    markers.forEach(m => {
      const dateMs = dayjs(m.date).valueOf();
      const color  = severityColor(m.severity);
      const di     = xAxis.makeDataItem({ value: dateMs });
      const range  = xAxis.createAxisRange(di);
      range.get("grid").setAll({ stroke: am5.color(color), strokeOpacity: 0.9, strokeDasharray: [8, 4] });
      range.get("label").setAll({
        text: m.text, rotation: 0, centerX: am5.p50, dy: -18, fontSize: 10, fill: am5.color(color),
        background: am5.RoundedRectangle.new(root, {
          fill: am5.color(color), fillOpacity: 0.08, stroke: am5.color(color),
          cornerRadiusTL: 10, cornerRadiusTR: 10, cornerRadiusBL: 10, cornerRadiusBR: 10
        }),
        paddingTop: 4, paddingBottom: 4, paddingLeft: 6, paddingRight: 6
      });
      markerRangesRef.current.push(range);
    });
  };

  const runDiagnosis = async () => {
    if (!athleteId) return;
    setDiagLoading(true);
    setDiagError('');
    const payload    = { athleteId, start: startISO, end: endISO, ...(trainerId ? { trainerId } : {}) };
    const controller = new AbortController();
    try {
      const callDiag = async (query) => {
        const { data: resp } = await axios.post(
          `${API}/pmc/ai-diagnosis${query}`, payload,
          { timeout: FRONT_MASTER_TIMEOUT_MS, signal: controller.signal }
        );
        return resp;
      };

      let resp;
      try {
        resp = await callDiag(`${IA_QUERY}`);
      } catch (err) {
        if (err?.code === 'ECONNABORTED') {
          resp = await callDiag('?provider=heuristic');
          setDiagError(t('pmcPane.diagErrorTimeout'));
        } else {
          throw err;
        }
      }

      const diagObj = {
        summary:
          resp?.summary || resp?.text ||
          (resp?.mode === 'demo' || resp?.mode === 'demo-fallback'
            ? t('pmcPane.diagErrorDemoMode')
            : t('pmcPane.diagErrorNoText')),
        findings:    Array.isArray(resp?.findings)    ? resp.findings    : [],
        suggestions: Array.isArray(resp?.suggestions) ? resp.suggestions : [],
        markers:     Array.isArray(resp?.markers)     ? resp.markers     : [],
        meta: resp?.meta || {
          usedLLM:    resp?.mode === 'live',
          usedModel:  resp?.usedModel || null,
          mode:       resp?.mode || 'demo-fallback',
          points:    (resp?.echo?.metrics?.series?.length) || (data?.length || 0)
        }
      };
      setDiag(diagObj);
      if (showMarkers && diagObj.markers.length) applyMarkers(diagObj.markers);
      else clearMarkers();

      if (resp?.error) {
        if (String(resp.error).includes('401')) setDiagError(t('pmcPane.diagError401'));
        else if (String(resp.error).includes('429')) setDiagError(t('pmcPane.diagError429'));
        else setDiagError(resp.error);
      }
    } catch (e) {
      console.error('IA diagnosis error:', e);
      const msg = e?.code === 'ECONNABORTED'
        ? t('pmcPane.diagErrorFrontTimeout')
        : (e?.response?.data?.error || e.message || t('pmcPane.diagErrorGeneric'));
      setDiagError(msg);
      clearMarkers();
    } finally {
      setDiagLoading(false);
    }
    return () => controller.abort();
  };

  useEffect(() => {
    if (!diag?.markers) return;
    if (showMarkers) applyMarkers(diag.markers);
    else clearMarkers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMarkers]);

  // ===== UI =====
  return (
    <div className="card p-2 pmc-card" style={{ width: '100%', maxWidth: '100%' }}>
      <h5 className="mb-2">{t('pmcPane.title')}</h5>

      <div className="d-flex flex-wrap gap-3 align-items-end mb-3">
        <div>
          <label className="form-label mb-1"><strong>{t('pmcPane.range')}</strong></label><br/>
          <div className="btn-group" role="group" aria-label={t('pmcPane.rangeDaysAriaLabel')}>
            {[30, 60, 90, 120].map(d => (
              <button key={d}
                className={`btn btn-sm ${daysRange === d ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setDaysRange(d)}
              >{d}d</button>
            ))}
          </div>
        </div>

        <div className="form-check form-switch ms-2">
          <input className="form-check-input" type="checkbox" id="whatIfSwitch"
                 checked={whatIf.enabled} onChange={e => setWhatIf(w => ({ ...w, enabled: e.target.checked }))}/>
          <label className="form-check-label" htmlFor="whatIfSwitch">
            <strong>{t('pmcPane.whatIf')}</strong>
          </label>
        </div>

        <div>
          <label className="form-label mb-1">{t('pmcPane.days')}</label>
          <input type="number" min={1} max={14} className="form-control form-control-sm"
                 value={whatIf.futureDays}
                 onChange={e => setWhatIf(w => ({ ...w, futureDays: Math.max(1, Math.min(14, Number(e.target.value)||1)) }))}/>
        </div>

        <div style={{ minWidth: 220 }}>
          <label className="form-label mb-1">{t('pmcPane.totalTss')}</label>
          <input type="range" min={0} max={1500} step={10} className="form-range"
                 value={whatIf.totalTss}
                 onChange={e => setWhatIf(w => ({ ...w, totalTss: Number(e.target.value) }))}/>
          <div style={{ fontSize: 12 }}>{whatIf.totalTss} TSS</div>
        </div>

        <div>
          <label className="form-label mb-1">{t('pmcPane.pattern')}</label>
          <select className="form-select form-select-sm" value={whatIf.pattern}
                  onChange={e => setWhatIf(w => ({ ...w, pattern: e.target.value }))}>
            <option value="even">{t('pmcPane.patternEven')}</option>
            <option value="polarized">{t('pmcPane.patternPolarized')}</option>
          </select>
        </div>

        {/* Botón Diagnóstico IA */}
        <div className="ms-auto d-flex align-items-end gap-2">
          <button className="btn btn-sm btn-outline-dark"
            onClick={runDiagnosis} disabled={diagLoading || !athleteId}
            title={t('pmcPane.diagButtonTitle')}
          >
            {diagLoading ? t('pmcPane.diagAnalyzing') : t('pmcPane.diagButton')}
          </button>
          <div className="form-check form-switch">
            <input className="form-check-input" type="checkbox" id="markersSwitch"
                   checked={showMarkers} onChange={e => setShowMarkers(e.target.checked)} />
            <label className="form-check-label" htmlFor="markersSwitch">
              {t('pmcPane.diagMarkersLabel')}
            </label>
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="row g-2 mb-2">
        <div className="col-12 col-md-3">
          <div className="p-2 border rounded">
            <div><strong>{t('pmcPane.todayTsb')}</strong></div>
            <div style={{ fontSize: 22 }}>{Number.isFinite(todayTsb) ? todayTsb.toFixed(2) : '-'}</div>
            <small className="text-muted">{t(hint.levelKey)}: {t(hint.msgKey)}</small>
          </div>
        </div>
        <div className="col-12 col-md-3">
          <div className="p-2 border rounded">
            <div><strong>{t('pmcPane.ctlToday')}</strong></div>
            <div style={{ fontSize: 22 }}>{ctlToday != null ? ctlToday.toFixed(1) : '-'}</div>
            <small className="text-muted">ΔCTL 7d: {rampCTL != null ? (rampCTL >= 0 ? '+' : '') + rampCTL.toFixed(1) : '-'}</small><br/>
            <small className="text-muted">{rampFlag ? `${t(rampFlag.levelKey)}: ${t(rampFlag.msgKey)}` : ''}</small>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="p-2 border rounded">
            <div><strong>{t('pmcPane.whatIfLabel')}</strong></div>
            <div style={{ fontSize: 16 }}>
              {whatIf.enabled
                ? t('pmcPane.whatIfProjection', { days: whatIf.futureDays, tsb: tsbProjectedEnd != null ? tsbProjectedEnd.toFixed(1) : '-', tss: whatIf.totalTss, pattern: whatIf.pattern })
                : t('pmcPane.whatIfDisabled')}
            </div>
            <small className="text-muted">{t('pmcPane.whatIfHint')}</small>
          </div>
        </div>
      </div>

      {/* Panel diagnóstico IA */}
      {diagError && <div className="alert alert-danger py-2">{diagError}</div>}
      {diag && (
        <div className="mb-2 p-2 border rounded bg-light">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <strong>{t('pmcPane.diagTitle')}</strong> {diag.summary}
              <div className="small text-muted">
                {t('pmcPane.diagSourceLLM') !== '' && (
                  <>{diag.meta?.usedLLM ? t('pmcPane.diagSourceLLM') : t('pmcPane.diagSourceHeuristic')} · {diag.meta?.points}</>
                )}
                {diag.meta?.usedModel ? ` · Modelo: ${diag.meta?.usedModel}` : ''}
                {diag.meta?.provider  ? ` · Provider: ${diag.meta?.provider}` : ''}
                {diag.meta?.model     ? ` · LLM: ${diag.meta?.model}` : ''}
                {diag.meta?.mode      ? ` · Modo: ${diag.meta?.mode}` : ''}
                {diag.meta?.elapsedMs ? ` · t=${diag.meta?.elapsedMs}ms` : ''}
              </div>
            </div>
            {Array.isArray(diag.markers) && diag.markers.length > 0 && (
              <div className="small">
                <span className="me-2"><span style={{display:'inline-block',width:10,height:10,background:'#ef4444',borderRadius:2}}/> {t('pmcPane.diagLegendRisk')}</span>
                <span className="me-2"><span style={{display:'inline-block',width:10,height:10,background:'#f59e0b',borderRadius:2}}/> {t('pmcPane.diagLegendTired')}</span>
                <span className="me-2"><span style={{display:'inline-block',width:10,height:10,background:'#10b981',borderRadius:2}}/> {t('pmcPane.diagLegendFresh')}</span>
              </div>
            )}
          </div>

          <div className="row mt-2 g-2">
            <div className="col-12 col-md-6">
              <div className="p-2 border rounded bg-white h-100">
                <div className="fw-semibold mb-1">{t('pmcPane.diagFindings')}</div>
                <ul className="mb-0">
                  {(diag.findings || []).map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            </div>
            <div className="col-12 col-md-6">
              <div className="p-2 border rounded bg-white h-100">
                <div className="fw-semibold mb-1">{t('pmcPane.diagSuggestions')}</div>
                <ul className="mb-0">
                  {(diag.suggestions || []).map((item, i) => <li key={i}>{item}</li>)}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && <p>{t('pmcPane.loading')}</p>}
      {!loading && (!data || data.length === 0) && <p>{t('pmcPane.noData')}</p>}

      <div ref={chartDivRef} style={{ width: '100%', height: chartHeight }}
           aria-label={t('pmcPane.chartAriaLabelAmCharts')} />

      <small className="text-muted d-block mt-2">{t('pmcPane.chartFootnoteAmCharts')}</small>
    </div>
  );
}
